import { Flex } from "@chakra-ui/react";
import { useCallback, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useForm, FormProvider } from "react-hook-form";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import { InvoiceFormData } from "@/types";
import InvoiceFileUpload from "@/components/InvoiceFileUpload";
import {
    createEmptyInvoiceMutationOptions,
    ocrScanInvoiceAttachmentMutationOptions,
    uploadAttachmentByPostMutationOptions
} from "@/api/queryOptions/invoiceAPIQueryOptions";
import { datadogRum } from "@datadog/browser-rum";

export const LandingPageFileUploader = () => {
    const methods = useForm<InvoiceFormData>({});
    const auth = useAuth();
    const navigate = useNavigate();
    const [isLoadingOcr, setIsLoadingOcr] = useState(false);

    const { getValues, setValue } = methods;

    const { mutate: createInvoice } = useMutation(createEmptyInvoiceMutationOptions);
    const { mutate: uploadAttachment } = useMutation(uploadAttachmentByPostMutationOptions);
    const { mutate: ocrScanAttachment } = useMutation(ocrScanInvoiceAttachmentMutationOptions);

    const updateFormAttachment = useCallback(
        (
            updater: (prev: NonNullable<InvoiceFormData["invoiceAttachment"]>) => InvoiceFormData["invoiceAttachment"]
        ) => {
            const current = getValues().invoiceAttachment;
            if (!current) {
                return;
            }
            setValue("invoiceAttachment", updater(current));
        },
        [getValues, setValue]
    );

    const markUploadFailed = useCallback(() => {
        updateFormAttachment(prev => ({
            ...prev,
            isUploading: false,
            isUploaded: false,
            uploadFailed: true
        }));
    }, [updateFormAttachment]);

    const markUploadSuccess = useCallback(() => {
        updateFormAttachment(prev => ({
            ...prev,
            isUploading: false,
            isUploaded: true,
            uploadFailed: false
        }));
    }, [updateFormAttachment]);

    const handleInvoiceCreated = useCallback(
        (createdInvoiceId: string, file: File) => {
            const form = new FormData();
            form.append("formFile", file);
            form.append("filename", file.name);

            uploadAttachment(
                {
                    id: createdInvoiceId,
                    form
                },
                {
                    onSuccess: attachmentId => {
                        updateFormAttachment(prev => ({
                            ...prev,
                            isUploading: false
                        }));
                        setIsLoadingOcr(true);
                        ocrScanAttachment(attachmentId, {
                            onSettled: () => {
                                setIsLoadingOcr(false);
                                markUploadSuccess();
                                navigate(`/invoice/${createdInvoiceId}`);
                            }
                        });
                    },
                    onError: markUploadFailed
                }
            );
        },
        [uploadAttachment, ocrScanAttachment, markUploadSuccess, navigate, markUploadFailed]
    );

    const handleUpload = useCallback(
        (file: File) => {
            datadogLogCreateInvoiceViaUpload();
            const actOnBehalfOf = globalThis.sessionStorage.getItem("actOnBehalfOf");
            const companyCrmId = actOnBehalfOf ? JSON.parse(actOnBehalfOf).crmId.toString() : "";

            updateFormAttachment(prev => ({
                ...prev,
                isUploading: true,
                isUploaded: false,
                uploadFailed: false
            }));

            createInvoice(
                {
                    crmId: companyCrmId || ""
                },
                {
                    onSuccess: createdInvoiceId => handleInvoiceCreated(createdInvoiceId, file),
                    onError: markUploadFailed
                }
            );
        },
        [createInvoice, handleInvoiceCreated, updateFormAttachment, markUploadFailed]
    );

    return (
        <Flex minW="100%" h="full" flexDir="column">
            <FormProvider {...methods}>
                {auth.user && (
                    <InvoiceFileUpload
                        name="invoiceAttachment"
                        onUpload={handleUpload}
                        accept={{
                            "application/pdf": [".pdf"],
                            "image/jpeg": [".tiff", ".tif", ".png", ".jpg", ".jpeg", ".gif"]
                        }}
                        isInvalid={false}
                        isLoadingOcr={isLoadingOcr}
                        id="invoiceOcrUpload"
                        dropzoneStyles={{
                            bg: "anchor.navy.50",
                            borderColor: "anchor.navy.200",
                            borderWidth: "1px",
                            borderStyle: "dashed",
                            h: "100%",
                            minH: "100%",
                            margin: 0
                        }}
                    />
                )}
            </FormProvider>
        </Flex>
    );
};

const datadogLogCreateInvoiceViaUpload = () => {
    try {
        datadogRum.addAction("create_invoice_via_upload");
    } catch {
        // Never block invoice creation flows if telemetry fails.
    }
};
